import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    MenuItem,
    Paper,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChefPartyTimeEnum } from "../../enums/ChefForPartyTimeEnums";
import { useStore } from "react-admin";
import ResourceSelectDialog from "../Common/ResourceSelectDialog";
import Customers from "../Customers/Customer";
import HouseList from "../Houses/HouseList";
import { setCustomSelectOpen } from "../../store/reducers/customSelect";
import { useAppDispatch } from "../../store/hooks";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import BookByDishService from "../../services/BookByDishService";
import { BookByDishCartResponse } from "../../interfaces/BookByDish/BookByDishCartResponse";
import HttpClient from "../../HttpClient";
import {
    BlueBorderContainer,
    BlueTOpBorderContainer,
    ButtonContainer,
    CustomTableRowTR,
    FilledButton,
    FormFieldGroup,
    FormLabel,
    InfoPill,
    SectionTitleRow,
    StyledDateText,
    StyledSelect,
    StyledTable,
    TableData,
    TableHeader,
    Title,
    WorkspaceContainer,
    WorkspaceHeader,
    WorkspaceIconBadge,
    WorkspacePanel,
    WorkspaceTitle,
} from "../../styles/commonStyles";
import { EditIcon, HouseIcon, UserIcon } from "../Svg";
import DishPickerRow, { DishOption } from "./DishPickerRow";
import { BookByDishCountUnit } from "../../enums/BookByDishCountUnit";
import { lineQuantityText } from "../../utils/BookByDishPlate";

const bookByDishService = new BookByDishService();

const showError = (message: string) => {
    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message,
        type: CustomSnackbarTypes.ERROR,
    });
};

const showSuccess = (message: string) => {
    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message,
        type: CustomSnackbarTypes.SUCCESS,
    });
};

const BookByDishCreateForm = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    // Used to wipe the stale mobile filter value persisted in React Admin's store for HouseList.
    const [, setHousesListParams] = useStore('Houses.listParams');
    const location = useLocation();
    const customerIdFromUrl = new URLSearchParams(location.search).get("customerId");

    const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
        customerIdFromUrl ? Number(customerIdFromUrl) : null
    );
    const [openCustomerDialog, setOpenCustomerDialog] = useState(false);

    const [houseId, setHouseId] = useState<number | null>(null);
    const [areaId, setAreaId] = useState<number | undefined>(undefined);
    const [openHouseDialog, setOpenHouseDialog] = useState(false);

    const [cart, setCart] = useState<BookByDishCartResponse | null>(null);
    const [loadingCart, setLoadingCart] = useState(false);
    const [cartFetched, setCartFetched] = useState(false);
    const [mutating, setMutating] = useState(false);

    const [date, setDate] = useState<string | null>(null);
    const [time, setTime] = useState<string | null>(null);

    const [creating, setCreating] = useState<boolean>(false);

    const [dishes, setDishes] = useState<DishOption[]>([]);

    const [noHousesFound, setNoHousesFound] = useState(false);

    // The cart item's embedded dish when present, else the catalogue entry (dishType / plateUnit).
    const getDish = (dishId: number, dish?: any): any =>
        dish ?? dishes.find((d) => d.id === dishId);

    // Load the Book by Dish dish catalogue once for the picker.
    useEffect(() => {
        bookByDishService
            .getBookByDishDishes()
            .then((list) => {
                const seen = new Set<number>();
                const unique = list
                    .map((d: any) => ({ id: d.id, name: d.name, dishType: d.dishType, plateSize: d.plateSize, plateUnit: d.plateUnit }))
                    .filter((d: DishOption) => {
                        if (seen.has(d.id)) return false;
                        seen.add(d.id);
                        return true;
                    });
                setDishes(unique);
            })
            .catch(() => showError("Failed to load dishes"));
    }, []);

    // Fetch houses for the selected customer.
    useEffect(() => {
        if (!selectedCustomerId) {
            setNoHousesFound(false);
            return;
        }
        HttpClient.get(`/centaurus/user/customer/${selectedCustomerId}/houses`)
            .then((res) => {
                const houses: any[] = res?.data?.data ?? res?.data ?? [];
                setNoHousesFound(houses.length === 0);
            })
            .catch(() => setNoHousesFound(false));
    }, [selectedCustomerId]);

    const handleChooseCustomer = () => {
        setOpenCustomerDialog(true);
        dispatch(setCustomSelectOpen({ open: true }));
    };

    const handleCloseCustomer = (record: any) => {
        setOpenCustomerDialog(false);
        if (!record?.id) return;
        setSelectedCustomerId(record.id);
        // reset downstream selections — a fresh booking
        setHouseId(null);
        setAreaId(undefined);
        setCart(null);
        setCartFetched(false);
        setDate(null);
        setTime(null);
        setNoHousesFound(false);
    };

    const handleChooseHouse = () => {
        if (noHousesFound) return;
        // Clear the stale mobile filter persisted in React Admin's store so the
        // HouseList always opens with a clean filter (no stale phone number).
        setHousesListParams({ filter: {}, displayedFilters: {}, order: 'ASC', page: 1, perPage: 10, sort: 'id' });
        setOpenHouseDialog(true);
        dispatch(setCustomSelectOpen({ open: true }));
    };

    const handleCloseHouse = (record: any) => {
        setOpenHouseDialog(false);
        if (!record?.id || !selectedCustomerId) return;
        setMutating(true);
        bookByDishService
            .updateCartSchedule(selectedCustomerId, { houseId: record.id })
            .then((updated) => {
                setHouseId(record.id);
                setCart(updated);
                setAreaId(updated?.areaId);
            })
            .catch((err: any) => {
                const message =
                    err?.response?.data?.message ||
                    err?.response?.data?.error ||
                    err?.message ||
                    "Failed to set house on cart";
                showError(message);
            })
            .finally(() => setMutating(false));
    };

    const fetchCart = useCallback(() => {
        if (!selectedCustomerId) return;
        setLoadingCart(true);
        setCartFetched(false);
        bookByDishService
            .getCart(selectedCustomerId)
            .then((res) => {
                setCart(res);
                setDate(res?.date ? moment(res.date).format("YYYY-MM-DD") : null);
                setTime(res?.time ?? null);
                // Pre-select the house/area already stored on the cart.
                setHouseId(res?.houseId ?? null);
                setAreaId(res?.areaId);
            })
            .catch(() => showError("Failed to fetch cart"))
            .finally(() => {
                setLoadingCart(false);
                setCartFetched(true);
            });
    }, [selectedCustomerId]);

    // Fetch the cart whenever the selected customer changes.
    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    const handleDateChange = (value: any) => {
        const formatted = value ? moment(value).format("YYYY-MM-DD") : null;
        setDate(formatted);
        if (!selectedCustomerId || !formatted) return;
        setMutating(true);
        bookByDishService
            .updateCartSchedule(selectedCustomerId, { date: formatted })
            .then((updated) => setCart(updated))
            .catch(() => showError("Failed to update date"))
            .finally(() => setMutating(false));
    };

    const handleTimeChange = (value: string) => {
        setTime(value);
        if (!selectedCustomerId || !value) return;
        setMutating(true);
        bookByDishService
            .updateCartSchedule(selectedCustomerId, { time: value })
            .then((updated) => setCart(updated))
            .catch(() => showError("Failed to update time"))
            .finally(() => setMutating(false));
    };

    // A PLATE upsert also makes the backend drop piece-based bread lines left in the cart by an
    // older customer build; they are re-rendered from the returned cart.
    const handleAddFromPicker = (dish: DishOption, count: number, _price?: number, _time?: number, countUnit?: BookByDishCountUnit) => {
        if (!selectedCustomerId) return;
        setMutating(true);
        bookByDishService
            .upsertCartItem(selectedCustomerId, dish.id, count, areaId, houseId ?? undefined, countUnit)
            .then((updated) => {
                setCart(updated);
                setAreaId(updated?.areaId);
                setCartFetched(true);
                showSuccess("Dish added");
            })
            .catch((err: any) => showError(err?.response?.data?.message || "Failed to add dish"))
            .finally(() => setMutating(false));
    };

    const handleRemoveItem = (dishId: number, dishName: string) => {
        if (!selectedCustomerId) return;
        EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
            message: `Are you sure you want to remove "${dishName}" from the cart?`,
            title: "Remove Dish",
            onClose: (confirmed: boolean) => {
                if (!confirmed) return;
                setMutating(true);
                bookByDishService
                    .removeCartItem(selectedCustomerId, dishId)
                    .then((updated) => {
                        setCart(updated);
                        showSuccess("Dish removed");
                    })
                    .catch(() => showError("Failed to remove dish"))
                    .finally(() => setMutating(false));
            },
        });
    };

    const handleCreateBooking = () => {
        if (!selectedCustomerId) return;
        if (!houseId) {
            showError("Choose a house for the booking");
            return;
        }
        if (!cart || !cart.items?.length) {
            showError("Cart is empty");
            return;
        }
        if (!date || !time) {
            showError("Select date and time");
            return;
        }
        setCreating(true);
        bookByDishService
            .checkout(selectedCustomerId, { houseId })
            .then((party) => {
                if (party?.id) {
                    showSuccess("Booking created");
                    // Dish bookings open under the BookByDish module so the
                    // sidebar highlights "Book by Dish".
                    navigate(`/BookByDish/${party.id}/show`, { replace: true });
                }
            })
            .catch(() => showError("Failed to create booking"))
            .finally(() => setCreating(false));
    };

    const handleDeleteCart = () => {
        if (!selectedCustomerId) return;
        EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
            message: "Are you sure you want to delete this cart?",
            title: "Confirm Delete",
            onClose: (confirmed: boolean) => {
                if (!confirmed) return;
                setMutating(true);
                bookByDishService
                    .deleteCart(selectedCustomerId)
                    .then(() => {
                        setCart(null);
                        setCartFetched(true);
                        showSuccess("Cart deleted");
                    })
                    .catch(() => showError("Failed to delete cart"))
                    .finally(() => setMutating(false));
            },
        });
    };

    return (
        <WorkspaceContainer>
            {openCustomerDialog && (
                <ResourceSelectDialog name={"Customers"} list={Customers} onClose={handleCloseCustomer} />
            )}
            {openHouseDialog && (
                <ResourceSelectDialog
                    name={"Houses"}
                    list={(props: any) => <HouseList {...props} filter={{ customerId: selectedCustomerId }} />}
                    onClose={handleCloseHouse}
                />
            )}

            {/* Header */}
            <WorkspaceHeader>
                <SectionTitleRow>
                    <WorkspaceIconBadge>
                        <EditIcon />
                    </WorkspaceIconBadge>
                    <div>
                        <WorkspaceTitle>Book by Dish</WorkspaceTitle>
                    </div>
                </SectionTitleRow>
                <ButtonContainer>
                    {cart && (
                        <InfoPill>
                            <Typography variant="body2" fontWeight={500}>Cart #{cart.id}</Typography>
                        </InfoPill>
                    )}
                </ButtonContainer>
            </WorkspaceHeader>

            {/* Main panel */}
            <WorkspacePanel>
                    {/* Customer / House / Area row */}
                    <BlueBorderContainer>
                        <Box display="flex" gap="1rem" alignItems="center" flexWrap="wrap">
                            <FilledButton onClick={handleChooseCustomer}>
                                <UserIcon color="#fff" width={20} height={20} />
                                {selectedCustomerId ? `Customer #${selectedCustomerId}` : "Choose Customer"}
                            </FilledButton>
                            {selectedCustomerId && (
                                noHousesFound ? (
                                    <FilledButton
                                        onClick={() => navigate(`/Houses/create?customerId=${selectedCustomerId}`)}
                                        disabled={mutating}
                                    >
                                        <HouseIcon />
                                        Add House
                                    </FilledButton>
                                ) : (
                                    <FilledButton onClick={handleChooseHouse} disabled={mutating}>
                                        <HouseIcon />
                                        {houseId ? `House #${houseId}` : "Choose House"}
                                    </FilledButton>
                                )
                            )}
                        </Box>
                        {noHousesFound && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                No house found for this customer. Create a house to create a cart.
                            </Typography>
                        )}
                        {(cart?.areaId ?? areaId) && (
                            <InfoPill>
                                <Typography variant="body2" fontWeight={500}>Area: {cart?.areaId ?? areaId}</Typography>
                            </InfoPill>
                        )}
                    </BlueBorderContainer>

                    {selectedCustomerId && (<>
                    {/* Date + Time + Dish picker — stacks vertically on mobile */}
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: "10px", mt: "20px", alignItems: { xs: "stretch", md: "flex-end" }, flexWrap: "wrap" }}>
                        {cart && (
                            <Box sx={{ width: { xs: "100%", md: "180px" }, flexShrink: 0 }}>
                                <FormFieldGroup style={{ width: "100%" }}>
                                    <FormLabel>Date</FormLabel>
                                    <FormControl style={{ width: "100%" }}>
                                        <DatePicker
                                            inputFormat="dd MMM yyyy"
                                            disableMaskedInput
                                            minDate={new Date()}
                                            value={date}
                                            onChange={handleDateChange}
                                            renderInput={(params: any) => (
                                                <StyledDateText {...params} label="" variant="outlined" />
                                            )}
                                        />
                                    </FormControl>
                                </FormFieldGroup>
                            </Box>
                        )}
                        {cart && (
                            <Box sx={{ width: { xs: "100%", md: "140px" }, flexShrink: 0 }}>
                                <FormFieldGroup style={{ width: "100%" }}>
                                    <FormLabel>Visiting Time</FormLabel>
                                    <FormControl style={{ width: "100%" }}>
                                        <StyledSelect
                                            variant="outlined"
                                            value={time ?? ""}
                                            onChange={(e) => handleTimeChange(String(e.target.value))}
                                        >
                                            {Object.values(ChefPartyTimeEnum).map((t) => (
                                                <MenuItem key={t} value={t}>
                                                    {t}
                                                </MenuItem>
                                            ))}
                                        </StyledSelect>
                                    </FormControl>
                                </FormFieldGroup>
                            </Box>
                        )}
                        <DishPickerRow
                            dishes={dishes}
                            areaId={areaId}
                            disabled={mutating}
                            onAdd={handleAddFromPicker}
                        />
                    </Box>

                    {/* Cart items + Summary */}
                    {loadingCart ? (
                        <Box py={3} display="flex" justifyContent="center">
                            <CircularProgress size={24} />
                        </Box>
                    ) : cart ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", mt: 3 }}>

                            {/* Cart Items */}
                            <Paper sx={{ borderRadius: "8px", boxShadow: "0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)", overflow: "hidden" }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: { xs: "12px 14px", sm: "16px 24px" } }}>
                                    <Title>Cart Items</Title>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        size="small"
                                        onClick={handleDeleteCart}
                                        disabled={mutating}
                                        sx={{ textTransform: "none", boxShadow: "none", flexShrink: 0 }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" sx={{ mr: "6px" }} />
                                        Delete Cart
                                    </Button>
                                </Box>
                                <TableContainer sx={{ overflowX: "auto" }}>
                                    <StyledTable style={{ tableLayout: "fixed", width: "100%", minWidth: 460 }}>
                                        <colgroup>
                                            <col style={{ width: "35%" }} />
                                            <col style={{ width: "20%" }} />
                                            <col style={{ width: "15%" }} />
                                            <col style={{ width: "15%" }} />
                                            <col style={{ width: "15%" }} />
                                        </colgroup>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: "rgba(232, 235, 237, 0.3) !important" }}>
                                                <TableHeader>Dish</TableHeader>
                                                <TableHeader style={{ textAlign: "center" }}>Count</TableHeader>
                                                <TableHeader style={{ textAlign: "center" }}>Price</TableHeader>
                                                <TableHeader style={{ textAlign: "center" }}>Time</TableHeader>
                                                <TableHeader style={{ textAlign: "center" }}></TableHeader>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {cart.items?.length ? (
                                                cart.items.map((item) => (
                                                    <CustomTableRowTR key={item.id}>
                                                        <TableData>{item.dish?.name ?? `Dish #${item.dishId}`}</TableData>
                                                        <TableData style={{ textAlign: "center" }}>
                                                            {lineQuantityText(item, getDish(item.dishId, item.dish))}
                                                        </TableData>
                                                        <TableData style={{ textAlign: "center" }}>₹{item.price}</TableData>
                                                        <TableData style={{ textAlign: "center" }}>{item.time} min</TableData>
                                                        <TableData style={{ textAlign: "center", padding: "8px" }}>
                                                            <Button
                                                                variant="contained"
                                                                color="error"
                                                                size="small"
                                                                onClick={() => handleRemoveItem(item.dishId, item.dish?.name ?? `Dish #${item.dishId}`)}
                                                                disabled={mutating}
                                                                sx={{ minWidth: "36px", width: "36px", height: "36px", p: 0, boxShadow: "none" }}
                                                            >
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </Button>
                                                        </TableData>
                                                    </CustomTableRowTR>
                                                ))
                                            ) : (
                                                <CustomTableRowTR>
                                                    <TableData colSpan={5} style={{ textAlign: "center", padding: "24px" }}>
                                                        Cart is empty — add a dish above.
                                                    </TableData>
                                                </CustomTableRowTR>
                                            )}
                                        </TableBody>
                                    </StyledTable>
                                </TableContainer>
                            </Paper>

                            {/* Summary — only when there are items */}
                            {cart.items && cart.items.length > 0 && (
                                <Paper sx={{ borderRadius: "8px", boxShadow: "0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)", overflow: "hidden" }}>
                                    <Box sx={{ p: { xs: "12px 14px", sm: "16px 24px" } }}>
                                        <Title>Summary</Title>
                                    </Box>
                                    <TableContainer sx={{ overflowX: "auto" }}>
                                        <StyledTable style={{ width: "100%", minWidth: 260 }}>
                                            <TableBody>
                                                <CustomTableRowTR>
                                                    <TableData>Dishes</TableData>
                                                    <TableData style={{ textAlign: "right", whiteSpace: "nowrap" }}>₹{cart.dishesPrice}</TableData>
                                                </CustomTableRowTR>
                                                <CustomTableRowTR>
                                                    <TableData>Visit Charges</TableData>
                                                    <TableData style={{ textAlign: "right", whiteSpace: "nowrap" }}>₹{cart.visitCharges}</TableData>
                                                </CustomTableRowTR>
                                                <CustomTableRowTR>
                                                    <TableData>Surge Price</TableData>
                                                    <TableData style={{ textAlign: "right", whiteSpace: "nowrap" }}>₹{Number((cart as any).surgePrice ?? 0)}</TableData>
                                                </CustomTableRowTR>
                                                <CustomTableRowTR>
                                                    <TableData style={{ fontWeight: 700 }}>Total</TableData>
                                                    <TableData style={{ textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" }}>₹{cart.totalPrice}</TableData>
                                                </CustomTableRowTR>
                                                <CustomTableRowTR>
                                                    <TableData>Total Time</TableData>
                                                    <TableData style={{ textAlign: "right", whiteSpace: "nowrap" }}>{cart.totalTime} min</TableData>
                                                </CustomTableRowTR>
                                            </TableBody>
                                        </StyledTable>
                                    </TableContainer>
                                </Paper>
                            )}
                        </Box>
                    ) : (
                        cartFetched && (
                            <Typography variant="body2" color="text.secondary" mt={2}>
                                No active cart. Add a dish to create one.
                            </Typography>
                        )
                    )}

                    {/* Create Booking */}
                    {cart && (
                        <BlueTOpBorderContainer>
                            <Typography variant="body2" color="text.secondary">
                                {!houseId
                                    ? "Choose a house to continue."
                                    : !date || !time
                                    ? "Select a date and time to continue."
                                    : `Booking total ₹${cart.totalPrice} · ${cart.items?.length ?? 0} dish(es)`}
                            </Typography>
                            <FilledButton
                                disabled={creating || !houseId || !cart.items?.length || !date || !time}
                                onClick={handleCreateBooking}
                                style={{ padding: '6px 32px' }}
                            >
                                {creating ? "Creating…" : "Create Booking"}
                            </FilledButton>
                        </BlueTOpBorderContainer>
                    )}
                    </>)}
                </WorkspacePanel>
        </WorkspaceContainer>
    );
};

export default BookByDishCreateForm;
